/**
 * @fileoverview Event List Section component.
 * @summary Displays list of events with loading and empty states.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/components/EventListSection
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useTranslation } from 'react-i18next'
import EventCard from '../../../components/EventCard.js'
import Spinner from '../../../components/Spinner.js'
import { EmptyStateCard } from './EmptyStateCard.js'
import type { Event } from '../../../store/slices/eventsSlice'

interface EventListSectionProps {
  isLoading: boolean
  events: Event[]
  filteredEvents: Event[]
  hasEvents: boolean
  onDeleteClick: (event: Event) => void
  onCreateEvent: () => void
}

/**
 * @summary Event list section component.
 * @param {EventListSectionProps} props - Component props.
 * @returns {React.ReactElement} Event list section.
 */
export function EventListSection({
  isLoading,
  filteredEvents,
  hasEvents,
  onDeleteClick,
  onCreateEvent,
}: EventListSectionProps) {
  const { t } = useTranslation()

  // Loading State
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spinner size="xl" />
      </div>
    )
  }

  // Empty State - No events at all
  if (!hasEvents) {
    return (
      <EmptyStateCard
        icon="🎤"
        title={t('dashboard.noEvents')}
        description={t('dashboard.noEventsDescription')}
        actionLabel={t('dashboard.createEvent')}
        onAction={onCreateEvent}
        isClickable={true}
      />
    )
  }

  // Empty State - No events matching filter
  if (filteredEvents.length === 0 && hasEvents) {
    return (
      <EmptyStateCard
        icon="🔍"
        title={t('dashboard.noEventsMatchingFilter')}
        description={t('dashboard.tryDifferentFilter')}
      />
    )
  }

  // Events Grid
  return (
    <div className="flex flex-col gap-4">
      {filteredEvents.map((event) => (
        <EventCard
          key={event.id}
          event={event}
          onDelete={() => onDeleteClick(event)}
        />
      ))}
    </div>
  )
}

